import React, { useState, useMemo } from 'react';
import { showToast } from '@/components/ui/Toast';
import { useTranslation } from 'react-i18next';
// 🛰️ Unified Shared Types
import { AppState, AppAction, KanbanTask, HouseholdContact, DataScope } from '@/types';
import { ContactCreator } from '../features/contacts/ContactCreator'; // NEW: Import ContactCreator
import { collection, addDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '@/firebase';
import {
  User, Phone, Mail, MapPin, History,
  Search, Plus, ShieldCheck, Star,
  ExternalLink, MessageSquare, Shield, Users, Layers, Lock, Trash2,
  Sparkles, Loader2
} from 'lucide-react';
import { useAiAction } from '@/hooks/useAiAction';

export const Contacts: React.FC<{ state: AppState; dispatch: React.Dispatch<AppAction> }> = ({ state, dispatch }) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContact, setSelectedContact] = useState<HouseholdContact | null>(null);
  const [activeDetailTab, setActiveDetailTab] = useState<'info' | 'history'>('info');
  const [showContactCreator, setShowContactCreator] = useState(false); // NEW: State to control ContactCreator visibility
  const [activeStream, setActiveStream] = useState<string>('all');
  const [activeSubCategory, setActiveSubCategory] = useState<string | null>(null);

  // --- 🛡️ HYBRID SCOPE STATE ---
  const currentUserId = state.currentUser?.id;
  const userRole = state.currentUser?.role;
  const hId = state.currentHouseholdId;
  const activeScope: DataScope = state.activeDataScope ?? (hId ? 'BOTH' : 'PERSONAL');

  // --- 🛰️ ENGINE DATA LINK ---
  const currentHousehold = state.households.find(h => h.id === hId);

  // Merge or Filter contacts based on the active scope
  const scopedContacts = useMemo(() => {
    // Correctly get contacts from the global state, which should be updated by listeners in App.tsx
    const allContacts = state.contacts || [];

    return allContacts.filter(contact => {
      if (activeScope === 'PERSONAL' && contact.scope === 'personal' && contact.ownerId === currentUserId) return true;
      if (activeScope === 'SHARED' && contact.scope === 'household' && contact.ownerId === hId) return true;
      if (activeScope === 'BOTH') {
        if (contact.scope === 'personal' && contact.ownerId === currentUserId) return true;
        if (contact.scope === 'household' && contact.ownerId === hId) return true;
      }
      return false;
    });
  }, [state.contacts, activeScope, currentUserId, hId]);

  // 🛰️ Filter Logic: Scans across Name, Company, Trade Specialty, Stream, and SubCategory
  const streamFilteredContacts = useMemo(() => {
    if (activeStream === 'all') return scopedContacts;
    const streamMap: Record<string, string> = {
      'service': 'manual', // map UI label to stream values
    };
    // Map UI stream labels to ContactStream values
    const streamValueMap: Record<string, string[]> = {
      'service-providers': ['manual', 'vehicle', 'maintenance', 'general'],
      'medical': ['medical'],
      'pet-care': ['pet'],
      'vehicle': ['vehicle'],
      'emergency': ['emergency'],
      'personal': ['childcare', 'legal', 'general'],
    };
    const allowed = streamValueMap[activeStream] ?? [activeStream];
    return scopedContacts.filter((c: HouseholdContact) => allowed.includes(c.stream));
  }, [scopedContacts, activeStream]);

  const filteredContacts = useMemo(() => {
    let result = streamFilteredContacts.filter((c: HouseholdContact) =>
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.subCategory?.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    if (activeSubCategory) {
      result = result.filter((c: HouseholdContact) =>
        c.subCategory?.toLowerCase() === activeSubCategory.toLowerCase()
      );
    }
    return result;
  }, [streamFilteredContacts, searchTerm, activeSubCategory]);

  const { callAction: callAi } = useAiAction(hId);
  const [duplicates, setDuplicates] = useState<Array<{ ids: string[]; names: string[]; reason: string }> | null>(null);
  const [duplicatesLoading, setDuplicatesLoading] = useState(false);
  const [enrichResults, setEnrichResults] = useState<Record<string, { notes?: string; suggestedTags?: string[] }>>({});
  const [enrichLoadingId, setEnrichLoadingId] = useState<string | null>(null);

  const handleFindDuplicates = async () => {
    setDuplicatesLoading(true);
    setDuplicates(null);
    try {
      const result = await callAi<{ duplicates: Array<{ ids: string[]; names: string[]; reason: string }> }>(
        'detectDuplicateContacts',
        { contacts: filteredContacts.map(c => ({ id: c.id, name: c.name, phone: c.phones?.[0]?.number, email: c.emails?.[0]?.address })) }
      );
      if (result) setDuplicates(result.duplicates);
    } finally {
      setDuplicatesLoading(false);
    }
  };

  const handleEnrichContact = async (contact: HouseholdContact) => {
    setEnrichLoadingId(contact.id);
    try {
      const result = await callAi<{ notes?: string; suggestedTags?: string[] }>(
        'enrichContact',
        { name: contact.name, companyName: contact.companyName, stream: contact.stream, subCategory: contact.subCategory }
      );
      if (result) setEnrichResults(prev => ({ ...prev, [contact.id]: result }));
    } finally {
      setEnrichLoadingId(null);
    }
  };

const handleAddContact = async (contactData: Partial<HouseholdContact>, targetScope: 'personal' | 'household') => {
    if (!currentUserId) {
      showToast(t('contacts.auth_required', 'You must be logged in to add a contact.'), 'error');
      return;
    }

    try {
            let contactRef;
      // Define an object without the 'id' initially, as Firestore generates it.
      let contactDataWithoutId: Omit<HouseholdContact, 'id'>;

      if (targetScope === 'personal') {
        contactRef = collection(db, 'users', currentUserId, 'contacts');
        contactDataWithoutId = {
          ...contactData,
          ownerId: currentUserId,
          scope: 'personal',
          isDeleted: false,
          localChangeTimestamp: Date.now(),
          phones: contactData.phones || [],
          emails: contactData.emails || [],
          tags: contactData.tags || [],
          name: contactData.name || 'Unknown', // Ensure name is always present
          category: contactData.category || 'personal',
          stream: contactData.stream || 'general',
        };
      } else { // 'household' scope
        if (!hId) {
          showToast(t('contacts.no_household_selected', 'Please select a household to add a shared contact.'), 'error');
          return;
        }
        contactRef = collection(db, 'households', hId, 'contacts');
        contactDataWithoutId = {
          ...contactData,
          ownerId: hId, // Household ID is the owner for shared contacts
          scope: 'household',
          isDeleted: false,
          localChangeTimestamp: Date.now(),
          phones: contactData.phones || [],
          emails: contactData.emails || [],
          tags: contactData.tags || [],
          name: contactData.name || 'Unknown', // Ensure name is always present
          category: contactData.category || 'personal',
          stream: contactData.stream || 'general',
        };
      }

      // Add to Firestore and get the generated document reference
      const docRef = await addDoc(contactRef, contactDataWithoutId);

      // Construct the final HouseholdContact with the Firestore-generated ID
      const finalContact: HouseholdContact = {
        ...contactDataWithoutId,
        id: docRef.id,
      };

      // Update local state
      dispatch({ type: 'ADD_CONTACT', payload: finalContact });
      setShowContactCreator(false);
      showToast(t('contacts.contact_added_success', 'Contact added successfully!'), 'success');

    } catch (error: any) {
      console.error('Error adding contact:', error);
      showToast(error.message || t('contacts.error_adding_contact', 'Failed to add contact.'), 'error');
    }
  };

  const handleDeleteContact = async (contactToDelete: HouseholdContact) => { // Takes full object
    // Debug log the selectedContact object here

    if (!currentUserId) {
      showToast(t('contacts.auth_required_delete', 'You must be logged in to delete a contact.'), 'error');
      return;
    }

    // Defensive check for contactId
    if (!contactToDelete.id || typeof contactToDelete.id !== 'string' || contactToDelete.id.trim() === '') {
      showToast(t('contacts.error_invalid_contact_id', 'Cannot delete contact: Invalid or missing contact ID.'), 'error');

      setSelectedContact(null);
      return;
    }

    if (!window.confirm(t('contacts.confirm_delete', 'Are you sure you want to delete this contact? This action cannot be undone.'))) {
      return;
    }

    try {
      let contactDocRef;
      if (contactToDelete.scope === 'personal') {

        contactDocRef = doc(db, 'users', currentUserId, 'contacts', contactToDelete.id);
      } else { // 'household' scope
        if (!hId) {
          showToast(t('contacts.no_household_selected_delete', 'Cannot delete shared contact without an active household.'), 'error');
          return;
        }

        contactDocRef = doc(db, 'households', hId, 'contacts', contactToDelete.id);
      }

      await deleteDoc(contactDocRef);
      dispatch({ type: 'REMOVE_CONTACT', payload: contactToDelete.id }); // Dispatch to remove from local state
      setSelectedContact(null); // Close the detail slide-over
      showToast(t('contacts.contact_deleted_success', 'Contact deleted successfully.'), 'success');

    } catch (error: any) {
      console.error('Error deleting contact:', error);
      showToast(error.message || t('contacts.error_deleting_contact', 'Failed to delete contact. Please try again.'), 'error');
    }
  };

  return (
    <div className="max-w-page-full mx-auto p-4 md:p-6 space-y-6 pb-32 animate-in fade-in duration-500">
      <header className="flex flex-col md:flex-row justify-between md:items-end gap-6">
        <div>
          <p className="text-2xs font-black text-brand uppercase tracking-[0.3em]">
            {hId ? t('contacts.subtitle', 'Hybrid Household Registry') : t('contacts.personal', 'Private Partition Registry')}
          </p>
        </div>

        <div className="flex items-center gap-2 self-end md:self-auto">
          <button
            onClick={handleFindDuplicates}
            disabled={duplicatesLoading || filteredContacts.length < 2}
            className="flex items-center gap-1.5 px-4 py-3 bg-amber-50 dark:bg-amber-900/20 text-amber-600 border border-amber-200 dark:border-amber-800 rounded-full text-2xs font-black uppercase tracking-widest hover:bg-amber-100 disabled:opacity-60 transition-colors"
          >
            {duplicatesLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Find Duplicates
          </button>
          <button
            onClick={() => setShowContactCreator(true)}
            className="bg-brand text-white p-4 rounded-full shadow-xl shadow-brand/20 hover:scale-105 active:scale-95 transition-all"
          >
            <Plus className="w-6 h-6" />
          </button>
        </div>
      </header>
      {duplicates && duplicates.length > 0 && (
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-[2rem] border border-amber-100 dark:border-amber-800 p-5 space-y-3">
          <p className="text-[9px] font-black uppercase tracking-widest text-amber-600">Possible Duplicates Found</p>
          {duplicates.map((d, i) => (
            <div key={i} className="flex items-center justify-between bg-white dark:bg-slate-900 rounded-xl px-4 py-3">
              <p className="text-xs font-bold text-slate-800 dark:text-white">{d.names.join(' & ')}</p>
              <p className="text-2xs text-slate-500">{d.reason}</p>
            </div>
          ))}
        </div>
      )}

      {/* --- SEARCH ARCHITECTURE --- */}
      <div className="relative group">
        <Search className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors w-5 h-5" />
        <input
          type="text"
          placeholder={t('contacts.search_placeholder', 'Search specialty or provider...')}
          value={searchTerm}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
          className="w-full pl-16 pr-6 py-5 bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-100 dark:border-slate-800 outline-none focus:ring-4 ring-blue-500/5 font-bold transition-all"
        />
      </div>

      {/* --- STREAM FILTER BAR --- */}
      <div className="flex flex-wrap gap-2">
        {[
          { id: 'all', label: 'All' },
          { id: 'service-providers', label: 'Service Providers' },
          { id: 'medical', label: 'Medical' },
          { id: 'pet-care', label: 'Pet Care' },
          { id: 'vehicle', label: 'Vehicle' },
          { id: 'emergency', label: 'Emergency' },
          { id: 'personal', label: 'Personal' },
        ].map(filter => (
          <button
            key={filter.id}
            onClick={() => { setActiveStream(filter.id); setActiveSubCategory(null); }}
            className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest transition-all ${
              activeStream === filter.id
                ? 'bg-brand text-white shadow-lg shadow-brand/20'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {/* --- SERVICE PROVIDER SUB-CATEGORY CHIPS --- */}
      {activeStream === 'service-providers' && (
        <div className="flex flex-wrap gap-2">
          {['Plumber', 'Electrician', 'HVAC', 'Landscaping', 'Cleaning', 'Other'].map(sub => (
            <button
              key={sub}
              onClick={() => setActiveSubCategory(activeSubCategory === sub ? null : sub)}
              className={`px-3 py-1.5 rounded-full text-2xs font-black uppercase tracking-widest transition-all ${
                activeSubCategory === sub
                  ? 'bg-blue-100 dark:bg-blue-900/40 text-brand border border-blue-300 dark:border-blue-700'
                  : 'border border-slate-200 dark:border-slate-700 text-slate-400 hover:border-blue-300 hover:text-blue-500'
              }`}
            >
              {sub}
            </button>
          ))}
        </div>
      )}

      {/* --- DIRECTORY GRID --- */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredContacts.map((contact: HouseholdContact) => (
          <button
            key={contact.id}
            onClick={() => {
                setSelectedContact(contact);
                setActiveDetailTab('info');
            }}
            className={`bg-white dark:bg-slate-900 p-8 rounded-[3rem] border text-left hover:shadow-2xl hover:-translate-y-1 transition-all group relative overflow-hidden ${
              contact.scope === 'personal' ? 'border-emerald-100 dark:border-emerald-900/30' : 'border-slate-100 dark:border-slate-800'
            }`}
          >
            <div className="flex justify-between items-start mb-6">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-500 ${
                contact.scope === 'personal'
                ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-500 group-hover:bg-emerald-500 group-hover:text-white'
                : 'bg-slate-50 dark:bg-slate-800 text-slate-400 group-hover:bg-brand group-hover:text-white'
              }`}>
                <User className="w-7 h-7" />
              </div>
              <div className="flex flex-col items-end gap-2">
                <span className={`text-[9px] font-black uppercase px-3 py-1.5 rounded-xl tracking-widest ${
                  contact.scope === 'personal' ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600' : 'bg-slate-50 dark:bg-slate-800 text-slate-500'
                }`}>
                  {contact.subCategory || 'Contractor'}
                </span>
                {contact.scope === 'personal' && <Lock className="w-3 h-3 text-emerald-500 opacity-40" />}
              </div>
            </div>
            <h3 className="font-black text-xl text-slate-900 dark:text-white leading-tight uppercase italic tracking-tighter">
              {contact.name}
            </h3>
            {contact.companyName && <p className="text-2xs font-black text-slate-400 uppercase tracking-widest mt-1">{contact.companyName}</p>}

            <div className="mt-8 flex gap-2">
              <div className="flex-1 h-12 bg-slate-50 dark:bg-slate-800 rounded-2xl flex items-center justify-center text-slate-400 group-hover:text-blue-500 transition-colors"><Phone className="w-4 h-4" /></div>
              <div className="flex-1 h-12 bg-slate-50 dark:bg-slate-800 rounded-2xl flex items-center justify-center text-slate-400 group-hover:text-blue-500 transition-colors"><Mail className="w-4 h-4" /></div>
            </div>
          </button>
        ))}
      </div>

      {/* --- CONTACT DETAIL SLIDE-OVER --- */}
      {selectedContact && (
        <div className="fixed inset-0 z-[100] flex justify-end">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-300" onClick={() => setSelectedContact(null)} />
          <div className="relative w-full max-w-xl bg-white dark:bg-slate-950 h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-500 ease-out">

            <header className="p-10 pb-0">
              <div className="flex justify-between items-start mb-8">
                <div className="flex gap-6 items-center">
                  <div className={`w-24 h-24 rounded-[2.5rem] flex items-center justify-center shadow-inner ${
                    selectedContact.scope === 'personal' ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-500' : 'bg-blue-50 dark:bg-blue-900/20 text-blue-500'
                  }`}>
                    <User className="w-12 h-12" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-4xl font-black text-slate-900 dark:text-white tracking-tighter leading-none uppercase italic">{selectedContact.name}</h2>
                      {selectedContact.scope === 'personal' && <Shield className="w-4 h-4 text-emerald-500" />}
                    </div>
                    <p className="text-2xs font-black text-blue-500 uppercase tracking-[0.3em] mt-3">{selectedContact.companyName || (selectedContact.scope === 'personal' ? 'Private Partition' : 'Household Registry')}</p>
                  </div>
                </div>
                {/* Close Button */}
                <button onClick={() => setSelectedContact(null)} className="p-3 bg-slate-50 dark:bg-slate-800 rounded-full hover:rotate-90 transition-all duration-300">
                  <Plus className="w-6 h-6 rotate-45 text-slate-400" />
                </button>
              </div>

              {/* Delete Button (Added Here) */}
              {selectedContact && (
                <button
                  onClick={() => handleDeleteContact(selectedContact)}
                  className="absolute top-4 right-20 p-2 rounded-full bg-rose-50 dark:bg-rose-900/20 text-rose-600 hover:bg-rose-100 dark:hover:bg-rose-900/40 transition-colors"
                  title="Delete Contact"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              )}
              {/* AI Enrich Button */}
              {selectedContact && (
                <button
                  onClick={() => handleEnrichContact(selectedContact)}
                  disabled={enrichLoadingId === selectedContact.id}
                  className="absolute top-4 right-36 p-2 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-500 hover:bg-blue-100 disabled:opacity-60 transition-colors"
                  title="AI Enrich"
                >
                  {enrichLoadingId === selectedContact.id ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
                </button>
              )}

              {/* Tabs Integration */}
              <div className="flex gap-10 border-b border-slate-100 dark:border-slate-800">
                {(['info', 'history'] as const).map((tID) => (
                  <button
                    key={tID}
                    onClick={() => setActiveDetailTab(tID)}
                    className={`pb-5 text-2xs font-black uppercase tracking-[0.3em] transition-all relative ${activeDetailTab === tID ? 'text-brand' : 'text-slate-400'}`}
                  >
                    {tID === 'info' ? t('contacts.info', 'Entity Details') : t('contacts.work_history', 'Ledger History')}
                    {activeDetailTab === tID && <div className="absolute bottom-0 left-0 w-full h-1 bg-brand rounded-t-full" />}
                  </button>
                ))}
              </div>
            </header>

            <div className="flex-1 overflow-y-auto p-10 no-scrollbar">
              {activeDetailTab === 'info' ? (
                <div className="space-y-8">
                  {/* AI Enrich Result */}
                  {selectedContact && enrichResults[selectedContact.id] && (
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-2xl border border-blue-100 dark:border-blue-800 space-y-2">
                      <p className="text-[9px] font-black uppercase tracking-widest text-blue-500">AI Insights</p>
                      {enrichResults[selectedContact.id].notes && <p className="text-xs text-slate-700 dark:text-slate-300">{enrichResults[selectedContact.id].notes}</p>}
                      {enrichResults[selectedContact.id].suggestedTags && enrichResults[selectedContact.id].suggestedTags!.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {enrichResults[selectedContact.id].suggestedTags!.map((tag, i) => (
                            <span key={i} className="text-[9px] font-black uppercase px-2 py-1 bg-blue-100 dark:bg-blue-900/40 text-brand rounded-lg">{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {/* Privacy Notice for Personal Contacts */}
                  {selectedContact.scope === 'personal' && (
                    <div className="flex items-center gap-4 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl">
                      <Lock className="w-5 h-5 text-emerald-500" />
                      <p className="text-2xs font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-tight">
                        This entity is stored in your private partition and is not visible to other household members.
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 gap-4">
                    {selectedContact.phones?.map((p) => (
                      <div key={p.number} className="flex items-center justify-between p-5 bg-slate-50 dark:bg-slate-900/50 rounded-3xl border border-slate-100 dark:border-slate-800 group hover:border-blue-200 transition-colors">
                        <div className="flex items-center gap-5">
                          <Phone className="w-5 h-5 text-blue-500" />
                          <div>
                            <p className="font-black text-slate-800 dark:text-slate-200 tabular-nums">{p.number}</p>
                            <p className="text-[9px] font-black uppercase text-slate-400 tracking-widest">{p.label}</p>
                          </div>
                        </div>
                        <ExternalLink className="w-4 h-4 text-slate-300 group-hover:text-brand transition-colors" />
                      </div>
                    ))}
                  </div>

                  {/* ... other detail components (MapPin, etc) ... */}
                </div>
              ) : (
                <div className="space-y-4">
                  {/* ... history logic ... */}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* --- ContactCreator Slide-over --- */}
      {showContactCreator && (
        <div className="fixed inset-0 z-[100] flex justify-end">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-300" onClick={() => setShowContactCreator(false)} />
          <ContactCreator
            onAdd={handleAddContact}
            onClose={() => setShowContactCreator(false)}
            currentUserId={currentUserId || ''}
            currentHouseholdId={hId}
            userRole={userRole}
          />
        </div>
      )}
    </div>
  );
};